/* import __COLOCATED_TEMPLATE__ from './signup-dropdown.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

const EMPTY_OPTIONS = [ ];

/**
 * @param {number} currentIndex
 * @param {any[]} options
 * @param {number} increment
 * @return number
 */
const getNextOption = (currentIndex, options, increment) => {
  let selectedIndex = currentIndex + increment;

  if (selectedIndex >= options.length) {
    selectedIndex = 0;
  } else if (selectedIndex < 0) {
    selectedIndex = options.length - 1;
  }

  return selectedIndex;
};

/** @module */
class SignupInput extends Component {
  @tracked hasFocus = false;
  @tracked isDirty = false;
  @tracked selectedIndex = -1;

  /**
   * @type {HTMLInputElement|null}
   */
  element = null;

  get useOptions() {
    return this.args.useOptions ?? true;
  }

  get options() {
    return this.args.options ?? EMPTY_OPTIONS;
  }

  get selectedOption() {
    const selectedIndex = this.selectedIndex;
    const options = this.options;

    return (selectedIndex > -1 && selectedIndex < options.length)
      ? options[selectedIndex]
      : null;
  }

  get showOptions() {
    if (!this.useOptions) return false;
    if (!this.hasFocus) return false;
    if (!this.isDirty) return false;

    const hasOptions = this.options.length > 0;
    const willShowEmptyIndicator = Boolean(this.args.value) && !this.args.isLoadingOptions;

    return hasOptions || willShowEmptyIndicator;
  }

  get highlightText() {
    return this.args.disableFiltering ? '' : this.args.value;
  }

  /**
   *
   */
  @action
  handleFocus() {
    this.hasFocus = true;
    this.isDirty = true;
    this.args.onfocus?.();
  }

  /**
   *
   */
  @action
  handleBlur() {
    this.hasFocus = false;
    this.isDirty = false;
    this.selectedIndex = -1;
    this.args.onblur?.();
  }

  /**
   * @param {string} value
   */
  @action
  handleInput(value) {
    this.isDirty = true;
    this.selectedIndex = -1;
    this.args.oninput?.(value);
  }

  /**
   * @param {KeyboardEvent} e
   */
  @action
  handleKeyDown(e) {
    this.args.onkeydown?.(e);

    if (e.defaultPrevented) {
      return;
    }

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        this.selectFromOptionsKeyboard(-1);
        break;

      case 'ArrowDown':
        e.preventDefault();
        this.selectFromOptionsKeyboard(+1);
        break;

      case 'Enter':
        const selectedOption = this.selectedOption;

        if (selectedOption) {
          e.preventDefault();
          this.isDirty = false;
          this.selectedIndex = -1;
          this.args.onchange?.(selectedOption);
        }

        break;

      case 'Escape':
        e.preventDefault();
        this.isDirty = false;
        this.selectedIndex = -1;
        break;
    }
  }

  /**
   * @param {HTMLInputElement} element
   */
  @action
  refCallback(element) {
    this.element = element;
    this.args.ref?.(element);
  }

  /**
   * @param {MouseEvent} e
   */
  @action
  handleClick(e) {
    this.isDirty = true;

    this.args.onclick?.(e);
  }

  /**
   * @param {Object} option
   */
  @action
  handleOptionClick(option) {
    this.isDirty = false;
    this.selectedIndex = -1;
    this.args.onchange?.(option);
  }

  @action
  handleChevronClick() {
    this.element.focus();
  }

  /**
   * @param {number} increment
   * @private
   */
  selectFromOptionsKeyboard(increment) {
    this.isDirty = true;

    if (this.showOptions) {
      const { options, selectedIndex } = this;

      if (options.length > 0) {
        this.selectedIndex = getNextOption(selectedIndex, options, increment);
      }
    }
  }
}

export default SignupInput;
